import {
    AVAILABILITY_STATUS_CHIP_CLASS,
    DAY_NAMES,
    MINUTES_PER_DAY,
} from '@apps/web/components/caregivers/caregiver_availability_schedule_constants';
import { type WeeklyScheduleSegment } from '@apps/web/components/caregivers/caregiver_availability_schedule_model';
import { cn } from '@apps/web/lib/utils';

// Compact enough that the full midnight-to-midnight axis fits in the
// caregiver modal without scrolling; marks every two hours keep the
// labels readable at this density.
const HOUR_HEIGHT_PX = 18;
const AXIS_START_MIN = 0;
const AXIS_END_MIN = MINUTES_PER_DAY;
const HOUR_MARKS = Array.from(
    { length: 11 },
    (unused, index) => AXIS_START_MIN + (index + 1) * 120,
);

// Day segments never cross midnight, so minute-of-day bounds are safe.
function getMinuteOfDayRange(segment: WeeklyScheduleSegment): {
    readonly start: number;
    readonly end: number;
} {
    const start = segment.startMinuteOfWeek % MINUTES_PER_DAY;
    return {
        start,
        end: start + (segment.endMinuteOfWeek - segment.startMinuteOfWeek),
    };
}

function formatHourLabel(minute: number): string {
    const hour = Math.floor(minute / 60) % 24;
    const hour12 = ((hour + 11) % 12) + 1;
    return `${hour12} ${hour < 12 ? 'AM' : 'PM'}`;
}

/**
 * Google-Calendar-style week grid of the recurring availability rules:
 * Monday-Sunday columns spanning midnight to midnight, with blocks
 * positioned by time of day. Day headers carry no dates because this
 * renders the rule, not a specific week.
 */
export function WeeklyPatternGrid({
    segments,
    onSegmentClick,
}: {
    readonly segments: ReadonlyArray<WeeklyScheduleSegment>;
    readonly onSegmentClick: (segment: WeeklyScheduleSegment) => void;
}) {
    const columnHeightPx =
        ((AXIS_END_MIN - AXIS_START_MIN) / 60) * HOUR_HEIGHT_PX;
    const minuteToPx = (minute: number) =>
        ((minute - AXIS_START_MIN) / 60) * HOUR_HEIGHT_PX;

    return (
        <div
            className="overflow-hidden rounded-lg border"
            data-testid="weekly-pattern-grid"
        >
            <div className="grid grid-cols-[3rem_repeat(7,minmax(0,1fr))]">
                <div className="bg-muted/30" />
                {DAY_NAMES.map((day) => (
                    <div
                        key={day}
                        className="bg-muted/30 text-muted-foreground border-border/70 border-l px-1 py-1.5 text-center text-[10px] font-semibold tracking-wide uppercase"
                    >
                        {day.slice(0, 3)}
                    </div>
                ))}
                <div className="relative" style={{ height: columnHeightPx }}>
                    {HOUR_MARKS.map((minute) => (
                        <span
                            key={minute}
                            className="text-muted-foreground absolute right-1 -translate-y-1/2 text-[10px] tabular-nums"
                            style={{ top: minuteToPx(minute) }}
                        >
                            {formatHourLabel(minute)}
                        </span>
                    ))}
                </div>
                {DAY_NAMES.map((day, dayIndex) => (
                    <div
                        key={day}
                        className="border-border/70 relative border-t border-l"
                        style={{ height: columnHeightPx }}
                        data-testid="weekly-pattern-day-column"
                        data-day={day.toLowerCase()}
                    >
                        {HOUR_MARKS.map((minute) => (
                            <div
                                key={minute}
                                className="border-border/50 absolute inset-x-0 border-t"
                                style={{ top: minuteToPx(minute) }}
                                aria-hidden={true}
                            />
                        ))}
                        {segments
                            .filter((segment) => segment.dayIndex === dayIndex)
                            .map((segment) => {
                                const range = getMinuteOfDayRange(segment);
                                const statusLabel =
                                    segment.status === 'unavailable'
                                        ? 'Unavailable'
                                        : 'Available';
                                return (
                                    <button
                                        key={segment.id}
                                        type="button"
                                        className={cn(
                                            'absolute inset-x-0.5 cursor-pointer overflow-hidden rounded-sm px-1 text-left text-[10px] leading-4 hover:opacity-80',
                                            AVAILABILITY_STATUS_CHIP_CLASS[
                                                segment.status
                                            ],
                                            segment.hasConflict &&
                                                'ring-1 ring-amber-400 ring-inset',
                                        )}
                                        style={{
                                            top: minuteToPx(range.start),
                                            height: Math.max(
                                                14,
                                                minuteToPx(range.end) -
                                                    minuteToPx(range.start),
                                            ),
                                        }}
                                        title={`${statusLabel} ${segment.timeLabel}${segment.sourceLabel ? ` · ${segment.sourceLabel}` : ''}`}
                                        aria-label={`${segment.dayLabel}: ${statusLabel} ${segment.timeLabel}`}
                                        onClick={() => onSegmentClick(segment)}
                                    >
                                        {segment.timeLabel}
                                    </button>
                                );
                            })}
                    </div>
                ))}
            </div>
        </div>
    );
}
